import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ComponentService } from 'src/app/service/component.service';
import { AdDirective } from './ad.directive';
import { EventService } from './event.service';
import { ComponentProperties, OverlayProperties } from './interfaces';

@Component({
  selector: 'overlay',
  templateUrl: './overlay-container.component.html',
  styleUrls: ['./overlay-container.component.sass'],
  standalone: false,
})
export class OverlayContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  public isInitial: boolean;
  globalEventsSubscription;
  public overlayBackdrop = true;
  minTimeout = 30;
  _hostOpacity = 0;

  @ViewChild(AdDirective, { static: true }) adHost: AdDirective;

  @Input() properties: OverlayProperties;

  @HostBinding('style.opacity')
  get hostOpacity() {
    return this.properties.fadeIn ? this._hostOpacity : 1;
  }
  set hostOpacity(value) {
    this._hostOpacity = value;
  }

  @HostBinding('style.transition')
  get hostTransition() {
    const duration = this.properties.animationDuration + 'ms ';
    const esing = this.properties.animationTimingFunction;
    return 'opacity ' + duration + esing + ', transform ' + duration + esing;
  }

  @HostBinding('style.transform') hostTransform: string;
  @HostBinding('style.z-index')
  get hostZIndex() {
    return this.properties.zIndex;
  }

  @HostBinding('style.width')
  get hostWidth() {
    return this.properties.wrapperWidth;
  }
  @HostBinding('style.height')
  get hostHeight() {
    return this.properties.wrapperHeight;
  }

  @HostBinding('style.position')
  get hostPosition() {
    return this.properties.position;
  }

  @HostBinding('class.overlay-shown') hostShown: boolean;

  @HostListener('document:click', ['$event'])
  onClick(event: any) {
    const srpToggleClicked =
      event.target.classList.contains('mat-slide-toggle-bar') ||
      event.target.classList.contains('mat-slide-toggle-thumb') ||
      event.target.classList.contains('mat-slide-toggle-thumb-container');
    const popoverCloseClicked =
      event.target.classList.contains('popover-close-button-icon') ||
      event.target.classList.contains('popover-close-button');
    const pdpLinkClicked =
      event.target.classList.contains('mat-icon') ||
      event.target.classList.contains('pdp') ||
      event.target.classList.contains('assignment');
    if (
      (!this.elementRef.nativeElement.contains(event.target) && !srpToggleClicked) ||
      popoverCloseClicked ||
      pdpLinkClicked
    ) {
      if (this.hostShown) {
        this.closeOverlay();
        this.componentService.selectedHotspotNumber$.next(0);
        this.componentService.selectedHotspotNumberFromImage$.next(0);
      }
    }
  }

  @HostListener('transitionend', ['$event'])
  transitionEnd(event: any) {
    if (event.target.tagName.toLowerCase() !== 'overlay') {
      return;
    }

    if (event.propertyName !== 'opacity') {
      return;
    }

    if (this.hostShown) {
      this.closeOverlayEnd();
    } else {
      this.hostShown = true;
      this.eventService.emitChangeEvent({
        type: 'Shown',
      });
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: any) {
    if (this.hostShown && this.closeOnScroll) this.closeOverlay();
  }

  @HostListener('window:orientationchange', ['$event'])
  onRotate(event: any) {
    if (this.hostShown && this.closeOnRotate) this.closeOverlayEnd();
  }

  get popoverClass() {
    if (this.properties.metadata) {
      return this.properties.metadata.popoverClass;
    }
  }

  get closeOnScroll() {
    if (this.properties.metadata) {
      return this.properties.metadata.closeOnScroll;
    }
  }

  get closeOnRotate() {
    if (this.properties.metadata) {
      return this.properties.metadata.closeOnRotate;
    }
  }

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    public elementRef: ElementRef,
    private eventService: EventService,
    private componentService: ComponentService
  ) {
    this.globalEventsSubscription = this.eventService.emitter.subscribe((event) => {
      this.handleGlobalEvents(event);
    });
  }

  ngOnInit() {
    this.loadComponent();
    this.initialOverlay();

    setTimeout(() => {
      this.isInitial = true;
    }, this.minTimeout); // Note: not good

    this.eventService.emitChangeEvent({
      type: 'Show',
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.showOverlayAnimation();
    }, this.minTimeout);
  }

  ngOnDestroy() {
    if (this.globalEventsSubscription) {
      this.globalEventsSubscription.unsubscribe();
    }

    this.eventService.emitChangeEvent({
      type: 'Hidden',
    });
  }

  initialOverlay() {
    this.hostTransform = 'translateY(' + this.properties.animationTranslateY + ')';
  }

  showOverlayAnimation() {
    this.hostOpacity = 1;
    this.hostTransform = 'translateY(0px)';
  }

  loadComponent() {
    const adItem = this.properties;
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(adItem.mainComponent);
    const viewContainerRef = this.adHost.viewContainerRef;

    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(componentFactory);
    const componentRefElement = (componentRef.location as ComponentProperties).nativeElement;

    // Set styles
    componentRefElement.style.width = this.properties.width;
    componentRefElement.style.height = this.properties.height;
    componentRefElement.style.maxWidth = this.properties.maxWidth;
    componentRefElement.style.minWidth = this.properties.minWidth;

    // Set classes
    if (typeof this.popoverClass === 'string') {
      componentRefElement.classList.add(this.popoverClass);
    }
    if (typeof this.popoverClass === 'object') {
      this.popoverClass.forEach((className: any) => {
        componentRefElement.classList.add(className);
      });
    }

    // Pass properties
    (componentRef.instance as ComponentProperties).overlayProperties = this.properties;
  }

  closeOverlay() {
    this.hostOpacity = 0;
    this.hostTransform = 'translateY(' + this.properties.animationTranslateY + ')';
  }

  closeOverlayEnd() {
    this.hostShown = false;
    this.eventService.emitChangeEvent({
      type: '[Overlay] Hide',
    });
  }

  handleGlobalEvents(event: any) {
    if (event.type === 'Hide') {
      this.closeOverlay();
    }
  }
}
